/**
 * DatabaseSwitcher Component
 *
 * Painel de desenvolvimento para gerenciar clientes/databases.
 * Abas: Trocar (com storage toggle) | Dados (sync + backup) | Schema
 *
 * @since 2026-01-09
 * @updated 2026-04-24 - Simplificado: 3 abas, storage dentro de Trocar
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ClientInfo {
  code: string;
  name: string;
  db_engine: string;
  database: string;
  is_ready: boolean;
  tables_count: number;
  last_sync: string | null;
}

interface DatabaseInfo {
  current: string;
  driver: string;
  host: string;
  port: number;
  name: string;
  available: string[];
  active_client: string | null;
  clients: ClientInfo[];
}

interface StorageInfo {
  active_client: string | null;
  storage_mode: 'local' | 'production';
  bucket: string | null;
}

interface SyncStatus {
  client: string;
  status: 'idle' | 'running' | 'completed' | 'failed';
  last_sync: string | null;
  elapsed_seconds: number | null;
  progress: number | null;
  current_step: string | null;
  error: string | null;
}

interface BackupFile {
  filename: string;
  size: number;
  size_formatted: string;
  last_modified: string;
  age_days: number;
}

interface BackupClient {
  name: string;
  hub_code: string;
  pg_backups: BackupFile[];
}

interface BackupListResponse {
  success: boolean;
  message: string;
  endpoint: string;
  clients: BackupClient[];
}

type TabId = 'switch' | 'data' | 'schema';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function cleanName(name: string): string {
  return name.replace(/ \(PostgreSQL\)/, '');
}

function relativeDate(dateStr: string | null): string {
  if (!dateStr) return 'Nunca';
  try {
    const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
    if (diff === 0) return 'Hoje';
    if (diff === 1) return 'Ontem';
    if (diff < 7) return `${diff}d`;
    if (diff < 30) return `${Math.floor(diff / 7)}sem`;
    return new Date(dateStr).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
  } catch {
    return 'Nunca';
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DatabaseSwitcher({ className = '' }: { className?: string }) {
  const isDev = import.meta.env.DEV ||
    import.meta.env.VITE_APP_ENV === 'development' ||
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1';

  const [dbInfo, setDbInfo] = useState<DatabaseInfo | null>(null);
  const [storageInfo, setStorageInfo] = useState<StorageInfo | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [tab, setTab] = useState<TabId>('switch');
  const [syncStatuses, setSyncStatuses] = useState<Record<string, SyncStatus>>({});
  const [syncingClient, setSyncingClient] = useState<string | null>(null);
  const [backups, setBackups] = useState<BackupListResponse | null>(null);
  const [backupsLoading, setBackupsLoading] = useState(false);
  const [restoringClient, setRestoringClient] = useState<string | null>(null);
  const [dataView, setDataView] = useState<'sync' | 'backup'>('sync');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // ---- Data fetching ----

  useEffect(() => {
    if (!isDev) return;
    fetch('/api/v1/core/dev/database')
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) {
          data.clients = (data.clients || []).filter((c: ClientInfo) => c.db_engine === 'postgresql');
          setDbInfo(data);
        }
      })
      .catch(() => {});

    fetch('/api/v1/core/dev/storage')
      .then(r => r.ok ? r.json() : null)
      .then(data => data && setStorageInfo(data))
      .catch(() => {});
  }, [isDev]);

  // Poll sync status
  useEffect(() => {
    if (!isDev) return;
    const poll = () => {
      fetch('/api/v1/core/dev/sync/status')
        .then(r => r.ok ? r.json() : {})
        .then(setSyncStatuses)
        .catch(() => {});
    };
    poll();
    const id = setInterval(poll, 3000);
    return () => clearInterval(id);
  }, [isDev]);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ---- Actions ----

  const switchClient = useCallback(async (code: string) => {
    if (!dbInfo || code === dbInfo.active_client) return setIsOpen(false);
    setIsLoading(true);
    try {
      const r = await fetch('/api/v1/core/dev/database/switch', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client: code }),
      });
      if (r.ok) setTimeout(() => window.location.reload(), 800);
    } catch { /* ignore */ }
    setIsLoading(false);
    setIsOpen(false);
  }, [dbInfo]);

  const syncClient = useCallback(async (code: string, incremental: boolean) => {
    setSyncingClient(code);
    try {
      await fetch('/api/v1/core/dev/client/sync', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client: code, incremental }),
      });
    } catch { /* ignore */ }
    setSyncingClient(null);
  }, []);

  const schemaUpdate = useCallback(async (database: string, dryRun: boolean) => {
    setIsLoading(true);
    try {
      await fetch('/api/v1/core/dev/schema/sync', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ database, dry_run: dryRun }),
      });
    } catch { /* ignore */ }
    setIsLoading(false);
  }, []);

  const loadBackups = useCallback(async () => {
    setBackupsLoading(true);
    try {
      const r = await fetch('/api/v1/core/dev/sync-hub/backups');
      if (r.ok) setBackups(await r.json());
    } catch { /* ignore */ }
    setBackupsLoading(false);
  }, []);

  const restoreBackup = useCallback(async (clientName: string) => {
    setRestoringClient(clientName);
    try {
      const r = await fetch('/api/v1/core/dev/sync-hub/restore', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client: clientName, engine: 'pg' }),
      });
      if (r.ok) {
        const data = await r.json();
        toast.success(`Backup restaurado: ${data.backup_file} (${data.backup_size})`);
      } else {
        const err = await r.json().catch(() => ({ detail: 'Erro desconhecido' }));
        toast.error(`Erro: ${err.detail}`);
      }
    } catch { toast.error('Erro de conexão'); }
    setRestoringClient(null);
  }, []);

  const switchStorageMode = useCallback(async (mode: 'local' | 'production') => {
    if (storageInfo?.storage_mode === mode) return;
    try {
      const r = await fetch('/api/v1/core/dev/storage/mode', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode }),
      });
      if (r.ok) setTimeout(() => window.location.reload(), 800);
    } catch { /* ignore */ }
  }, [storageInfo]);

  // ---- Guards ----

  if (!isDev || !dbInfo) return null;

  const clients = dbInfo.clients || [];
  const activeClient = clients.find(c => c.code === dbInfo.active_client);
  const displayName = activeClient ? cleanName(activeClient.name) : dbInfo.name;
  const runningSyncs = Object.values(syncStatuses).filter(s => s.status === 'running');

  // ---- Tabs config ----

  const TABS: { id: TabId; label: string; icon: string }[] = [
    { id: 'switch', label: 'Trocar', icon: 'bi-arrow-left-right' },
    { id: 'data', label: 'Dados', icon: 'bi-cloud-download' },
    { id: 'schema', label: 'Schema', icon: 'bi-table' },
  ];

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      {/* Trigger Button */}
      <button
        onClick={() => { setIsOpen(!isOpen); if (!isOpen && tab === 'data' && dataView === 'backup' && !backups) loadBackups(); }}
        disabled={isLoading}
        className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-sm transition
          hover:bg-muted disabled:opacity-50
          ${runningSyncs.length > 0 ? 'ring-2 ring-blue-400/50' : ''}`}
        title={`${displayName} | PostgreSQL`}
      >
        {runningSyncs.length > 0 ? (
          <i className="bi bi-arrow-repeat animate-spin text-blue-500" />
        ) : (
          <i className="bi bi-database text-[#336791]" />
        )}
        <span className="max-w-[120px] truncate">{displayName}</span>
        <span className="text-[10px] px-1 py-0.5 rounded font-bold bg-[#33679120] text-[#336791]">PG</span>
        {runningSyncs.length > 0 && (
          <span className="text-[10px] px-1 py-0.5 rounded font-bold bg-blue-100 dark:bg-blue-900/30 text-blue-500">
            {runningSyncs.length} sync
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute bottom-full mb-1 right-0 w-[380px] bg-popover rounded-lg shadow-2xl border border-border z-50 overflow-hidden">

          {/* Tab Bar */}
          <div className="flex border-b border-border">
            {TABS.map(t => (
              <button
                key={t.id}
                onClick={() => { setTab(t.id); if (t.id === 'data' && dataView === 'backup' && !backups) loadBackups(); }}
                className={`flex-1 px-2 py-2 text-xs font-medium transition border-b-2 ${
                  tab === t.id
                    ? 'text-foreground/80 border-blue-500 bg-muted'
                    : 'text-muted-foreground border-transparent hover:text-foreground'
                }`}
              >
                <i className={`bi ${t.icon} mr-1`} />{t.label}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="max-h-[360px] overflow-y-auto">

            {/* ===== TROCAR TAB ===== */}
            {tab === 'switch' && (
              <>
                {/* Storage toggle dentro de Trocar */}
                {storageInfo && (
                  <div className="px-3 py-2 bg-muted border-b border-border flex items-center justify-between text-[10px]">
                    <span className="text-muted-foreground font-medium">Storage:</span>
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => switchStorageMode('local')}
                        className={`px-2 py-0.5 rounded transition ${
                          storageInfo.storage_mode === 'local'
                            ? 'bg-blue-500 text-white font-medium'
                            : 'text-muted-foreground hover:bg-muted'
                        }`}
                      >
                        <i className="bi bi-hdd mr-0.5" /> MinIO
                      </button>
                      <button
                        onClick={() => switchStorageMode('production')}
                        className={`px-2 py-0.5 rounded transition ${
                          storageInfo.storage_mode === 'production'
                            ? 'bg-green-500 text-white font-medium'
                            : 'text-muted-foreground hover:bg-muted'
                        }`}
                      >
                        <i className="bi bi-cloud mr-0.5" /> S3 Prod
                      </button>
                    </div>
                  </div>
                )}
                {clients.map(client => {
                  const active = client.code === dbInfo.active_client;
                  return (
                    <button
                      key={client.code}
                      onClick={() => switchClient(client.code)}
                      disabled={isLoading || active}
                      className={`w-full px-3 py-2.5 flex items-center justify-between text-left transition border-b border-border last:border-0 ${
                        active
                          ? 'bg-green-50 dark:bg-green-900/10'
                          : 'hover:bg-muted'
                      } disabled:cursor-default`}
                    >
                      <div className="flex items-center gap-2">
                        {active && <i className="bi bi-check-circle-fill text-green-500" />}
                        <span className={`text-sm font-medium ${active ? 'text-green-700 dark:text-green-300' : 'text-foreground'}`}>
                          {cleanName(client.name)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                        <span>{client.tables_count} tabelas</span>
                        <span className="font-bold text-[#336791]">PG</span>
                      </div>
                    </button>
                  );
                })}
              </>
            )}

            {/* ===== DADOS TAB (Sync + Backup) ===== */}
            {tab === 'data' && (
              <>
                {/* Sub-toggle: Sync / Backup */}
                <div className="px-3 py-2 bg-muted border-b border-border flex items-center gap-1.5">
                  <button
                    onClick={() => setDataView('sync')}
                    className={`flex-1 px-2 py-1 text-[11px] font-medium rounded transition ${
                      dataView === 'sync'
                        ? 'bg-green-500 text-white'
                        : 'text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    <i className="bi bi-cloud-download mr-1" />Sync Produção
                  </button>
                  <button
                    onClick={() => { setDataView('backup'); if (!backups) loadBackups(); }}
                    className={`flex-1 px-2 py-1 text-[11px] font-medium rounded transition ${
                      dataView === 'backup'
                        ? 'bg-amber-500 text-white'
                        : 'text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    <i className="bi bi-database-down mr-1" />Backup Hub
                  </button>
                </div>

                {/* Sync view */}
                {dataView === 'sync' && clients.map(client => {
                  const status = syncStatuses[client.code];
                  const isRunning = status?.status === 'running';
                  const progress = status?.progress || 0;
                  return (
                    <div key={client.code} className="px-3 py-2.5 border-b border-border last:border-0">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-sm font-medium text-foreground">
                          {cleanName(client.name)}
                        </span>
                        <span className="text-[10px] text-muted-foreground">{relativeDate(client.last_sync)}</span>
                      </div>
                      {isRunning ? (
                        <div>
                          <div className="w-full bg-gray-200 dark:bg-black/20 rounded-full h-1.5 mb-1">
                            <div className="bg-blue-500 h-1.5 rounded-full transition-all" style={{ width: `${progress}%` }} />
                          </div>
                          <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                            <span><i className="bi bi-gear-fill animate-spin mr-1" />{status?.current_step}</span>
                            <span>{progress}%</span>
                          </div>
                        </div>
                      ) : (
                        <div className="flex gap-1.5">
                          <button
                            onClick={() => syncClient(client.code, false)}
                            disabled={!!syncingClient}
                            className="flex-1 px-2 py-1.5 text-[10px] font-medium rounded transition bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/40 disabled:opacity-40"
                          >
                            {syncingClient === client.code ? <i className="bi bi-arrow-repeat animate-spin" /> : <><i className="bi bi-cloud-download mr-1" />Full</>}
                          </button>
                          <button
                            onClick={() => syncClient(client.code, true)}
                            disabled={!!syncingClient}
                            className="flex-1 px-2 py-1.5 text-[10px] font-medium rounded transition bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/40 disabled:opacity-40"
                          >
                            <i className="bi bi-arrow-down-short mr-1" />Incremental
                          </button>
                        </div>
                      )}
                      {status?.status === 'failed' && status.error && (
                        <div className="mt-1.5 text-[10px] text-red-500 bg-red-50 dark:bg-red-900/20 rounded p-1.5">
                          <i className="bi bi-exclamation-triangle mr-1" />{status.error}
                        </div>
                      )}
                      {status?.status === 'completed' && (
                        <div className="mt-1 text-[10px] text-green-500"><i className="bi bi-check2-all mr-1" />Concluído</div>
                      )}
                    </div>
                  );
                })}

                {/* Backup view */}
                {dataView === 'backup' && (
                  <>
                    {backupsLoading && (
                      <div className="p-6 text-center text-muted-foreground">
                        <i className="bi bi-arrow-repeat animate-spin text-lg block mb-2" />
                        <span className="text-xs">Conectando ao MinIO Hub...</span>
                      </div>
                    )}
                    {!backupsLoading && backups && !backups.success && (
                      <div className="p-4 text-center">
                        <i className="bi bi-exclamation-triangle text-amber-500 text-lg block mb-2" />
                        <p className="text-xs text-muted-foreground">{backups.message}</p>
                        <button
                          onClick={loadBackups}
                          className="mt-2 text-[10px] px-3 py-1 rounded bg-muted text-muted-foreground hover:bg-muted/80 transition"
                        >
                          <i className="bi bi-arrow-clockwise mr-1" />Tentar novamente
                        </button>
                      </div>
                    )}
                    {!backupsLoading && backups?.success && backups.clients.length === 0 && (
                      <div className="p-4 text-center text-xs text-muted-foreground">
                        Nenhum backup encontrado no Hub MinIO.
                      </div>
                    )}
                    {!backupsLoading && backups?.success && backups.clients.map(bc => (
                      <div key={bc.name} className="border-b border-border last:border-0">
                        <div className="px-3 py-2.5">
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="text-sm font-medium text-foreground">{bc.name}</span>
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 font-medium">
                              {bc.pg_backups.length} backup{bc.pg_backups.length !== 1 ? 's' : ''}
                            </span>
                          </div>

                          {bc.pg_backups.length > 0 && (
                            <div className="space-y-1">
                              {bc.pg_backups.map((b, i) => (
                                <div key={b.filename} className="flex items-center justify-between text-[10px] p-1.5 rounded bg-muted">
                                  <div className="flex items-center gap-1.5 text-muted-foreground min-w-0">
                                    <i className="bi bi-file-earmark-zip" />
                                    <span className="truncate">{b.filename}</span>
                                  </div>
                                  <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                                    <span className="text-muted-foreground">{b.size_formatted}</span>
                                    <span className={`${b.age_days <= 1 ? 'text-green-500' : b.age_days <= 7 ? 'text-amber-500' : 'text-red-400'}`}>
                                      {b.age_days === 0 ? 'hoje' : `${b.age_days}d`}
                                    </span>
                                    {i === 0 && (
                                      <button
                                        onClick={() => restoreBackup(bc.name)}
                                        disabled={!!restoringClient}
                                        className="px-2 py-0.5 rounded font-medium bg-amber-500 text-white hover:bg-amber-600 transition disabled:opacity-40"
                                        title="Restaurar este backup no banco local"
                                      >
                                        {restoringClient === bc.name ? (
                                          <i className="bi bi-arrow-repeat animate-spin" />
                                        ) : (
                                          <><i className="bi bi-download mr-0.5" />Restaurar</>
                                        )}
                                      </button>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                    {!backupsLoading && backups?.success && (
                      <div className="px-3 py-2 bg-muted border-t border-border flex items-center justify-between text-[10px] text-muted-foreground">
                        <span><i className="bi bi-hdd-network mr-1" />{backups.endpoint}</span>
                        <button onClick={loadBackups} className="hover:text-muted-foreground transition" title="Atualizar lista">
                          <i className="bi bi-arrow-clockwise" />
                        </button>
                      </div>
                    )}
                  </>
                )}
              </>
            )}

            {/* ===== SCHEMA TAB ===== */}
            {tab === 'schema' && clients.map(client => (
              <div key={client.code} className="px-3 py-2.5 border-b border-border last:border-0">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-sm font-medium text-foreground">
                    {cleanName(client.name)}
                  </span>
                  <span className="text-[10px] text-muted-foreground font-mono">{client.database}</span>
                </div>
                <div className="flex gap-1.5">
                  <button
                    onClick={() => schemaUpdate(client.database, true)}
                    disabled={isLoading}
                    className="flex-1 px-2 py-1.5 text-[10px] font-medium rounded transition bg-muted text-muted-foreground hover:bg-muted/80 disabled:opacity-40"
                  >
                    <i className="bi bi-eye mr-1" />Preview
                  </button>
                  <button
                    onClick={() => schemaUpdate(client.database, false)}
                    disabled={isLoading}
                    className="flex-1 px-2 py-1.5 text-[10px] font-medium rounded transition bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400 hover:bg-purple-100 dark:hover:bg-purple-900/40 disabled:opacity-40"
                  >
                    <i className="bi bi-table mr-1" />Aplicar
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="px-3 py-1.5 bg-muted border-t border-border flex items-center justify-between text-[10px] text-muted-foreground">
            <span>
              <i className="bi bi-lightning-charge-fill text-green-500 mr-1" />
              {displayName}
            </span>
            <span className="font-bold text-[#336791]">PostgreSQL</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default DatabaseSwitcher;
